import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Box, useMediaQuery } from "@mui/material";
import { ResponsiveBar, BarCustomLayer, BarDatum } from "@nivo/bar";
import { useTheme } from "@mui/material/styles";
import { ViewMode } from "./financialPeriodConfig";
import ChartHeader, { ToggleOption } from "./ChartHeader";
import ChartEmptyState from "./ChartEmptyState";
import ChartCardWrapper from "./ChartCardWrapper";
import ChartModal from "./ChartModal";
import { createBarValueLabelsLayer } from "./BarValueLabelsLayer";
import ChartTooltipPortal from "./ChartTooltipPortal";

const getTickLabels = (
  allLabels: string[],
  isMobile: boolean,
): string[] | undefined => {
  const count = allLabels.length;

  let step: number | undefined;
  if (count > 40) {
    step = Math.ceil(count / (isMobile ? 8 : 12));
  } else if (isMobile) {
    if (count <= 8) step = undefined;
    else if (count <= 16) step = 2;
    else if (count <= 24) step = 3;
    else step = 4;
  } else {
    if (count <= 12) step = undefined;
    else if (count <= 24) step = 2;
    else step = 3;
  }

  if (!step) return undefined;
  return allLabels.filter((_, i) => i % step === 0);
};

// Minimal row shape this chart actually consumes: an index label (`period`) plus
// one numeric key per bar series. `TransformedDataPoint` is structurally
// assignable to it, so every existing ticker-chart caller compiles unchanged;
// the AI-chat custom-chart consumer supplies bespoke comparison rows (spec §6.3a).
export type GenericBarDatum = { period: string; [key: string]: unknown };

export interface GenericFinancialBarChartProps {
  title: string;
  ticker?: string;
  height?: number;
  isModal?: boolean;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  onCloseModal?: () => void;

  isLoading: boolean;
  error: Error | null;

  chartData: GenericBarDatum[];
  viewMode?: ViewMode;

  barKeys: string[];
  indexBy?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  colors: string[] | ((bar: any) => string);
  groupMode?: "grouped" | "stacked";

  yAxisLegend?: string;
  yAxisFormat: (value: number) => string;
  valueLabelsFormat?: (value: number) => string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipContent?: (bar: any) => React.ReactNode;

  growthMetrics?: React.ReactNode;
  belowChartContent?: React.ReactNode;

  minValue?: number | "auto";
  leftMargin?: number;

  noDataTitle?: string;
  noDataDescription?: string;

  // Optional internal header controls (period toggle + custom actions). When
  // keepHeaderWhenEmpty is set, the header (and its controls) stays visible in the
  // loading/empty/error state so the user can still switch period.
  headerToggleOptions?: ToggleOption[];
  headerToggleValue?: string;
  onHeaderToggleChange?: (value: string) => void;
  headerActions?: React.ReactNode;
  headerCenterContent?: React.ReactNode;
  keepHeaderWhenEmpty?: boolean;

  // Extra Nivo layers drawn above the bars (e.g. a CAGR trend line).
  extraLayers?: BarCustomLayer<BarDatum>[];

  // Cap individual bar width (px). Increases padding when few bars would otherwise
  // render too wide. Tuned for single-series charts.
  maxBarWidth?: number;

  // Suppress the built-in value labels (e.g. when a chart provides its own custom
  // label layer via extraLayers). Label headroom above the bars is still reserved.
  hideValueLabels?: boolean;

  // Optional Nivo bar styling pass-throughs (e.g. outlined / pattern-filled bars).
  borderWidth?: number;
  borderColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defs?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fill?: any[];

  // Extra space below the bars (e.g. to fit below-bar value labels) and how far the
  // x-axis tick labels sit beneath the axis.
  bottomMargin?: number;
  axisBottomTickPadding?: number;
  // X-axis tick label rotation (default -45, tuned for dense period labels).
  // Sparse categorical charts (e.g. chat comparison charts) pass 0.
  axisBottomTickRotation?: number;
}

const GenericFinancialBarChart: React.FC<GenericFinancialBarChartProps> = (
  props
) => {
  const {
    title,
    height = 400,
    isModal = false,
    renderCard = true,
    showFullscreen = false,
    noHover = false,
    onCloseModal,
    isLoading,
    error,
    chartData,
    viewMode,
    barKeys,
    indexBy = "period",
    colors,
    groupMode = "grouped",
    yAxisLegend,
    yAxisFormat,
    valueLabelsFormat,
    tooltipContent,
    growthMetrics,
    belowChartContent,
    minValue = "auto",
    leftMargin,
    noDataTitle,
    noDataDescription,
    headerToggleOptions,
    headerToggleValue,
    onHeaderToggleChange,
    headerActions,
    headerCenterContent,
    keepHeaderWhenEmpty = false,
    extraLayers = [],
    maxBarWidth,
    hideValueLabels = false,
    borderWidth,
    borderColor,
    defs,
    fill,
    bottomMargin,
    axisBottomTickPadding,
    axisBottomTickRotation,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [modalOpen, setModalOpen] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  // Measure the plot width so maxBarWidth can be enforced via Nivo padding.
  // Re-runs when the chart becomes visible (chartData length 0 -> N).
  useEffect(() => {
    if (!maxBarWidth) return;
    const el = chartContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMeasuredWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [maxBarWidth, chartData.length]);

  // Mobile-only: tap-toggled persistent tooltip, mirroring RevenueSegmentationChart.
  // Nivo's hover tooltip is bypassed on mobile because it closes on touchend.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mobileTooltip, setMobileTooltip] = useState<{ bar: any; x: number; y: number } | null>(null);
  const mobileTouchOriginRef = useRef<{ y: number } | null>(null);

  useEffect(() => {
    if (!isMobile || !mobileTooltip) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) mobileTouchOriginRef.current = { y: t.clientY };
      if (!chartContainerRef.current?.contains(e.target as Node)) {
        setMobileTooltip(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !mobileTouchOriginRef.current) return;
      if (Math.abs(t.clientY - mobileTouchOriginRef.current.y) > 10) {
        setMobileTooltip(null);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [isMobile, mobileTooltip]);

  const reserveLabelSpace = !isMobile && !noHover;
  const showValueLabels = reserveLabelSpace && !hideValueLabels;
  const textColor = theme.palette.text.primary;

  // Callers pass yAxisFormat/barKeys as inline literals, so a dependency-keyed
  // useMemo would mint a NEW layer component every render and Nivo would
  // unmount/remount the layer each time (once per streamed token while a chat
  // reply renders). Create the layer ONCE with a getter that always reads the
  // latest config from a ref — stable identity, fresh config.
  const labelsConfig = { yAxisFormat: valueLabelsFormat ?? yAxisFormat, groupMode, barKeys, textColor, isModal };
  const labelsConfigRef = useRef(labelsConfig);
  labelsConfigRef.current = labelsConfig;
  const [valueLabelsLayer] = useState(() => createBarValueLabelsLayer(() => labelsConfigRef.current));

  if (isLoading || error || !chartData || chartData.length === 0) {
    return (
      <ChartCardWrapper renderCard={renderCard}>
        {keepHeaderWhenEmpty && (
          <ChartHeader
            title={title}
            show={!noHover}
            toggleOptions={headerToggleOptions}
            toggleValue={headerToggleValue}
            onToggleChange={onHeaderToggleChange}
            actions={headerActions}
            centerContent={headerCenterContent}
            showFullscreen={false}
            isModal={isModal}
            onCloseModal={onCloseModal}
          />
        )}
        <ChartEmptyState
          isLoading={isLoading}
          error={error}
          isEmpty={!isLoading && !error && (!chartData || chartData.length === 0)}
          height={height}
          title={title}
          noDataTitle={noDataTitle}
          noDataDescription={noDataDescription}
        />
      </ChartCardWrapper>
    );
  }

  const bottomTickValues = (() => {
    if (isModal) return undefined;
    const allLabels = chartData.map((d) => d.period);
    return getTickLabels(allLabels, isMobile);
  })();

  const effectiveMaxValue = (() => {
    const allValues = chartData.flatMap((d) =>
      barKeys.map((key) => (d[key] as number) ?? 0)
    );
    const dataMax = Math.max(...allValues);
    return dataMax < 0 ? 0 : "auto";
  })();

  const effectiveLeftMargin = leftMargin ?? (isMobile ? 70 : 100);

  const basePadding = Math.min(0.6, Math.max(0.3, 1 - chartData.length * 0.08));
  const effectivePadding = (() => {
    if (!maxBarWidth || measuredWidth <= 0 || chartData.length === 0) return basePadding;
    const rightMargin = isMobile ? 10 : 20;
    const innerWidth = measuredWidth - effectiveLeftMargin - rightMargin;
    if (innerWidth <= 0) return basePadding;
    const required = 1 - (maxBarWidth * chartData.length) / innerWidth;
    return Math.min(0.92, Math.max(basePadding, required));
  })();

  const chartContent = (
    <>
      <Box sx={isModal ? { px: 2, pt: 2 } : undefined}>
        <ChartHeader
          title={title}
          show={!noHover}
          toggleOptions={headerToggleOptions}
          toggleValue={headerToggleValue}
          onToggleChange={onHeaderToggleChange}
          actions={headerActions}
          centerContent={headerCenterContent}
          showFullscreen={showFullscreen}
          isModal={isModal}
          onOpenModal={() => setModalOpen(true)}
          onCloseModal={onCloseModal}
        />
      </Box>

      <Box
        ref={chartContainerRef}
        onClick={isMobile ? (e: React.MouseEvent) => {
          // Dismiss tooltip when tapping empty chart space; stop propagation
          // so the parent card's onClick (modal open) doesn't also fire.
          if (mobileTooltip) {
            e.stopPropagation();
            setMobileTooltip(null);
          }
        } : undefined}
        sx={{
          height,
          ...(isModal && { flex: 1, minHeight: 0 }),
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.015)",
          borderRadius: 1,
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <ResponsiveBar
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data={chartData as any}
          keys={barKeys}
          indexBy={indexBy}
          margin={{
            top: isMobile ? 10 : reserveLabelSpace ? 30 : 20,
            right: isMobile ? 10 : 20,
            bottom: bottomMargin ?? (isMobile ? 60 : 70),
            left: effectiveLeftMargin,
          }}
          padding={effectivePadding}
          innerPadding={groupMode === "grouped" ? (isModal ? 10 : 4) : 0}
          groupMode={groupMode}
          valueScale={{ type: "linear", min: minValue, max: effectiveMaxValue }}
          indexScale={{ type: "band", round: true }}
          colors={colors}
          borderRadius={2}
          borderWidth={borderWidth ?? 0}
          {...(borderColor ? { borderColor } : {})}
          {...(defs ? { defs } : {})}
          {...(fill ? { fill } : {})}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: axisBottomTickPadding ?? (isMobile ? 14 : 20),
            tickRotation: axisBottomTickRotation ?? -45,
            tickValues: bottomTickValues,
            format: (value) => value.toString().replace(/ \(\d+\)$/, ""),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: isMobile ? 8 : 5,
            tickRotation: 0,
            legend: isMobile ? undefined : yAxisLegend,
            legendPosition: "middle",
            legendOffset: -(effectiveLeftMargin - 20),
            format: yAxisFormat,
            tickValues: isMobile ? 4 : 5,
          }}
          enableLabel={false}
          enableGridX={!isMobile}
          enableGridY={true}
          gridYValues={isMobile ? 4 : 5}
          layers={[
            "grid",
            "axes",
            "bars",
            ...extraLayers,
            ...(showValueLabels ? [valueLabelsLayer] : []),
            "markers",
            "legends",
            "annotations",
          ]}
          theme={{
            background: "transparent",
            text: {
              fontSize: isMobile ? 10 : 12,
              fill: theme.palette.text.primary,
              outlineWidth: 0,
              outlineColor: "transparent",
            },
            axis: {
              domain: {
                line: { stroke: "transparent", strokeWidth: 0 },
              },
              legend: {
                text: {
                  fontSize: isMobile ? 10 : 12,
                  fill: theme.palette.text.primary,
                  fontWeight: 600,
                },
              },
              ticks: {
                line: { stroke: "transparent", strokeWidth: 0 },
                text: {
                  fontSize: isMobile ? 10 : 11,
                  fill: theme.palette.text.primary,
                  fontWeight: 600,
                },
              },
            },
            grid: {
              line: {
                stroke: theme.palette.divider,
                strokeWidth: 1,
                strokeOpacity: 0.2,
              },
            },
            tooltip: {
              container: tooltipContent
                ? {
                    background: "transparent",
                    border: "none",
                    borderRadius: 0,
                    boxShadow: "none",
                    padding: 0,
                  }
                : {
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    fontSize: 12,
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[8],
                    border: `1px solid ${theme.palette.divider}`,
                  },
            },
          }}
          isInteractive={!noHover}
          {...(!noHover && tooltipContent
            ? {
                // On mobile the hover tooltip is replaced by tap-toggle state below.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tooltip: isMobile ? () => null : (bar: any) => {
                  const rect = chartContainerRef.current?.getBoundingClientRect();
                  const anchorX = rect
                    ? rect.left + effectiveLeftMargin + (bar.x ?? 0) + (bar.width ?? 0) / 2
                    : undefined;
                  return (
                    <ChartTooltipPortal anchorX={anchorX} containerRef={chartContainerRef}>
                      {tooltipContent(bar)}
                    </ChartTooltipPortal>
                  );
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick: isMobile ? (bar: any, event: React.MouseEvent) => {
                  // Stop propagation so the parent card wrapper's onClick
                  // (which opens the fullscreen modal) doesn't also fire.
                  event.stopPropagation();
                  setMobileTooltip(prev =>
                    prev?.bar.index === bar.index && prev?.bar.id === bar.id
                      ? null
                      : { bar, x: event.clientX, y: event.clientY }
                  );
                } : undefined,
              }
            : {})}
        />
      </Box>

      {isModal && growthMetrics}

      {isModal && !growthMetrics && (
        <Box sx={{ position: "relative", pb: 1.5 }}>
          {belowChartContent}
          <Box
            component="img"
            src={
              theme.palette.mode === "dark"
                ? "/insightLongLogoPrimaryWhiteText.svg"
                : "/insightLongLogoPrimary.svg"
            }
            alt="Insight Analytics"
            sx={{
              height: 32,
              display: "block",
              position: "absolute",
              right: 16,
              bottom: 12,
            }}
          />
        </Box>
      )}

      {(!isModal || growthMetrics) && belowChartContent}

      {isMobile && mobileTooltip && tooltipContent && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            left: Math.max(148, Math.min(mobileTooltip.x, window.innerWidth - 148)),
            top: mobileTooltip.y - 24,
            transform: "translate(-50%, -100%)",
            zIndex: 99999,
            pointerEvents: "none",
          }}
        >
          {tooltipContent(mobileTooltip.bar)}
        </div>,
        document.body
      )}
    </>
  );

  return (
    <>
      <ChartCardWrapper renderCard={renderCard} isModal={isModal}>
        {chartContent}
      </ChartCardWrapper>

      {!isModal && (
        <ChartModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <GenericFinancialBarChart
            {...props}
            isModal={true}
            renderCard={false}
            height={700}
            showFullscreen={false}
            onCloseModal={() => setModalOpen(false)}
          />
        </ChartModal>
      )}
    </>
  );
};

export default GenericFinancialBarChart;
